import { ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { ClusterNav } from "./ClusterNav";
import { ClusterFooter } from "./ClusterFooter";
import { ClusterBreadcrumb } from "./ClusterBreadcrumb";
import { AdSlot } from "@/components/AdSlot";
import { Toaster } from "@/components/ui/sonner";
import { LocaleProvider } from "@/i18n/LocaleContext";
import type { Locale } from "@/i18n/types";
import { translatePathToEn, translatePathToId } from "@/i18n/paths";
import { withSlash } from "@/lib/url";
import { SITE_ORIGIN } from "@/lib/seo/schema";

export function SiteShell({ children, locale }: { children: ReactNode; locale?: Locale }) {
  return (
    <LocaleProvider locale={locale}>
    <div className="flex min-h-screen flex-col" data-locale={locale ?? "id"}>
      <HreflangAlternates locale={locale ?? "id"} />
      <Header />
      <ClusterNav />
      <ClusterBreadcrumb />
      <AdSlot size="leaderboard" position="header" className="hidden md:flex" />
      <AdSlot size="mobile-banner" position="header-mobile" className="md:hidden" />
      <main className="container mx-auto flex-1 px-4 py-6 sm:px-6 sm:py-8">{children}</main>
      <AdSlot size="leaderboard" position="footer" className="hidden md:flex" />
      <ClusterFooter />
      <Footer />
      <Toaster />
    </div>
    </LocaleProvider>
  );
}

/**
 * Render <link rel="alternate" hreflang="…"> tags for the current page.
 * React 19 hoists <link> elements into <head> automatically, so this
 * works for both SSR (search engine indexing) and client navigation.
 *
 * We compute the canonical Indonesian path from the current pathname,
 * then derive the EN equivalent. Both link to URLs with trailing slash
 * to match our canonical URL shape.
 */
function HreflangAlternates({ locale: _locale }: { locale: Locale }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Canonicalise to Indonesian path (no /en prefix, ID segments).
  let idPath: string;
  if (pathname === "/en" || pathname === "/en/") {
    idPath = "/";
  } else if (pathname.startsWith("/en/")) {
    idPath = translatePathToId(pathname.slice(3)) || "/";
  } else {
    idPath = pathname || "/";
  }
  // Skip diagnostic / API routes entirely.
  if (pathname.startsWith("/api/") || pathname.startsWith("/__")) return null;
  const idUrl = `${SITE_ORIGIN}${withSlash(idPath)}`;
  // ID-only sections that have no EN counterpart route. Emitting an
  // alternate hreflang="en" for these would point Google at non-existent
  // /en/... URLs (519+ "Not found 404" reports in Search Console).
  const ID_ONLY_PREFIXES = [
    "/jadwal-sholat",
    "/jadwal-imsak",
    "/jadwal-buka-puasa",
    "/hari/",
    "/zodiak",
    "/harpitnas",
    "/kalkulator",
    "/tools/hari-jadian",
    "/tools/hari-lahir",
    "/tools/hari-ldr",
    "/tools/hari-menikah",
    "/tools/hari-putus",
    "/tools/hari-sahabat",
  ];
  const isIdOnly =
    idPath === "/hari" ||
    ID_ONLY_PREFIXES.some((p) => idPath === p || idPath.startsWith(p));
  if (isIdOnly) {
    return (
      <>
        <link rel="alternate" hrefLang="id" href={idUrl} />
        <link rel="alternate" hrefLang="x-default" href={idUrl} />
      </>
    );
  }
  const enRel = translatePathToEn(idPath);
  const enPath = idPath === "/" ? "/en/" : `/en${enRel}`;
  const enUrl = `${SITE_ORIGIN}${withSlash(enPath)}`;
  return (
    <>
      <link rel="alternate" hrefLang="id" href={idUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={idUrl} />
    </>
  );
  // unused locale param retained for future per-locale tweaks
}